import { useCallback, useMemo, useState } from "react";
import { notification } from "antd";
import { useIdleTimer } from "react-idle-timer";
import { useTranslation } from "react-i18next";
import { useActiveDocumentSet } from "../../documentSet";
import { Status } from "../../common/status/status";
import { ButtonType } from "../../common";
import { useAppSelector } from "../../app";
import { throttle } from "../../common/utilities/func";
import documentIdsSelector from "../../document/selectors/documentIdsSelector";
import useRouting from "./useRouting";
import highestGroupBlockCountSelector from "../../configMap/selectors/highestGroupBlockCountSelector";
import numberOfUserAnnotationsSelector from "../../annotation/selectors/numberOfUserAnnotationsSelector";
import { useSaveMetricEventMutation } from "../../analytics/store/analyticsApi";
import selectAnnotationReducer from "../../annotation/selectors/annotationReducerSelector";
import useValidateBlockingCategorizations from "../../metadata/hooks/useValidateBlockingCategorizations";
import useValidateBlockingEntities from "../../metadata/hooks/useValidateBlockingEntities";
import { postData } from "../../configMap/utils/entityUtils";
import { calculateHideDelay } from "../../common/utilities/text";
import { getErrorStatus } from "../utils/errorHelpers";
import useHoldReasonPicker from "./useHoldReasonPicker";
import { HoldReason } from "../../documentTypes/interfaces";

const useActionButton = (
  status: Status,
  buttonType: ButtonType,
  message: string,
  setIsLoading: (loading: boolean) => void,
  openUserConflictModal?: () => void,
) => {
  const { t } = useTranslation("errors");

  const { getTotalIdleTime, getTotalActiveTime } = useIdleTimer({
    timeout: 5000,
    debounce: 500,
  });

  const user = useAppSelector((state) => state.userReducer.user);
  const entityAnnotations = useAppSelector(selectAnnotationReducer);

  const { categorizationAnnotations } = useAppSelector(
    (state) => state.metadataReducer.categorizationState,
  );
  const numberOfLines = useAppSelector(highestGroupBlockCountSelector);
  const numberOfAnnotations = useAppSelector(numberOfUserAnnotationsSelector);
  const { numberOfAdditions, numberOfUpdates, numberOfDeletions } =
    useAppSelector((state) => state.analyticsReducer);

  const documentIds = useAppSelector(documentIdsSelector);

  const { updateAssigneeAndStatusWithExceptionHandling, activeDocumentSet } =
    useActiveDocumentSet();

  const handleRouting = useRouting(buttonType);

  const validateBlockingCategorizations = useValidateBlockingCategorizations();
  const validateBlockingEntities = useValidateBlockingEntities();

  const [saveMetricEvent] = useSaveMetricEventMutation();

  const holdReasonPicker = useHoldReasonPicker(buttonType, status);
  const { showPicker } = holdReasonPicker;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // `useMemo` so `throttle()` itself is memoized instead of re-created and discarded
  // every render, as it would be as a `useCallback` argument.
  const handleApproval = useMemo(
    () =>
      throttle(async (holdReason?: HoldReason) => {
        if (buttonType === ButtonType.Approve) {
          const categorizationsValid = validateBlockingCategorizations();
          const entitiesValid = validateBlockingEntities();
          if (!categorizationsValid || !entitiesValid) {
            notification.error({
              title: t("requiredFieldsError"),
            });
            return;
          }
        }

        if (activeDocumentSet?.documents) {
          // The footer loader replaces this button and the modal with it, so a confirm from
          // the modal spins the confirm button instead.
          if (showPicker) {
            setIsConfirming(true);
          } else {
            setIsLoading(true);
          }

          try {
            await postData(
              documentIds,
              entityAnnotations,
              categorizationAnnotations || [],
            );
            await updateAssigneeAndStatusWithExceptionHandling(
              activeDocumentSet,
              status,
              user!,
              true,
              holdReason,
            );

            await saveMetricEvent({
              documentSetId: activeDocumentSet.id,
              organizationName: user?.organization.name ?? "",
              activeTimeSeconds: getTotalActiveTime() / 1000,
              idleTimeSeconds: getTotalIdleTime() / 1000,
              numberOfAdditions,
              numberOfDeletions,
              numberOfUpdates,
              numberOfLines,
              numberOfAnnotations,
            }).unwrap();

            setIsModalOpen(false);

            notification.success({
              title: "Success",
              description: message,
              duration: calculateHideDelay(message),
              onClose: () => {
                handleRouting(() => setIsLoading(false));
              },
            });
          } catch (error) {
            if (getErrorStatus(error) === 409 && openUserConflictModal) {
              openUserConflictModal();
            } else {
              const errorMessage = (error as { message: string }).message;
              notification.error({
                title: "Error",
                description: errorMessage,
                duration: calculateHideDelay(errorMessage),
              });
            }
          } finally {
            setIsConfirming(false);
            setIsLoading(false);
          }
        }
      }, 250),
    [
      categorizationAnnotations,
      user,
      numberOfDeletions,
      numberOfAdditions,
      numberOfUpdates,
      activeDocumentSet,
      setIsLoading,
      updateAssigneeAndStatusWithExceptionHandling,
      status,
      message,
      entityAnnotations,
      getTotalActiveTime,
      getTotalIdleTime,
      saveMetricEvent,
      numberOfLines,
      numberOfAnnotations,
      documentIds,
      handleRouting,
      openUserConflictModal,
      validateBlockingCategorizations,
      validateBlockingEntities,
      buttonType,
      showPicker,
      t,
    ],
  );

  // The cached reason list can be stale (e.g. it was empty when last fetched, and a reason
  // was linked since), so the list is always refetched before deciding whether to show the
  // picker at all - deciding from the stale cache would let a hold through with no reason.
  const handleClick = useCallback(async () => {
    if (!holdReasonPicker.isConfigured) {
      handleApproval();
      return;
    }

    const { data } = await holdReasonPicker.refetch();

    if (data && data.length > 0) {
      setIsModalOpen(true);
      return;
    }

    handleApproval();
  }, [holdReasonPicker, handleApproval]);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  return {
    holdReasonPicker,
    isModalOpen,
    isConfirming,
    closeModal,
    handleClick,
    handleApproval,
  };
};

export default useActionButton;
